import { IPatient } from '../../../../constants/types';
import { SEX_FEMALE, SEX_MALE } from './demographics';

/**
 * SHER-583. The Female/Male split both ICD-11 diagnosis tables render.
 *
 * It is computed here, in the client, and cannot be computed anywhere else: patient sex is part
 * of the encrypted personal data, so the API never sees it. All the API can send is patient ids
 * — the outpatient diagnosis endpoint always did, and the inpatient discharge one gained them in
 * SHER-583 precisely so that both tables could show the same columns.
 *
 * This lives in `utils/` rather than inside either table because otherwise the second table to
 * need it copies the first one's loop, and the two drift. That has already happened once in this
 * feature: three hand-written copies of the diagnosis-label lookup in the API drifted apart at
 * the cost of ~42 production exceptions in a week.
 */

/** Lookup from patient id to the sex on their decrypted personal data. */
export const buildPatientSexMap = (patients: IPatient[]): Map<number, string> => {
  const sexById = new Map<number, string>();

  (patients || []).forEach((patient) => {
    if (patient && patient.id && patient.personalData && patient.personalData.sex) {
      sexById.set(patient.id, patient.personalData.sex);
    }
  });

  return sexById;
};

export interface SexSplit {
  female: number;
  male: number;
}

/**
 * Counts Female and Male among `patientIds`.
 *
 * A patient the store has not decrypted, or one whose sex is recorded as neither value, is
 * counted in NEITHER column — deliberately. `female + male` is therefore allowed to be less than
 * the row's total, and the Total column is read from the API's own distinct-patient count rather
 * than derived from this, so a partly-decrypted store understates the split without ever
 * understating the total.
 */
export const sexSplitFor = (patientIds: number[] | undefined, sexById: Map<number, string>): SexSplit => {
  let female = 0;
  let male = 0;

  (patientIds || []).forEach((patientId) => {
    const sex = sexById.get(patientId);
    // The vocabulary is `demographics.ts`'s, not a second copy of it: both analytics readings of
    // patient sex compare against the same two constants, so they cannot drift apart.
    if (sex === SEX_FEMALE) {
      female += 1;
    } else if (sex === SEX_MALE) {
      male += 1;
    }
  });

  return { female, male };
};
